import { memo, useCallback, useEffect, useRef, useState, type ComponentRef } from "react";
import { Platform } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { DialogModal } from "@src/components/DialogModal";
import { isWeb } from "@src/utils/web-helpers";
import { Autocomplete, Input } from "@visioni/a3s-ui/components";
import {
  OBJECT_TYPE_LABELS,
  OBJECT_TYPE_DESCRIPTIONS,
  OBJECT_TYPES,
  type ObjectType,
} from "@visioni/a3s-dumping/constants";

interface ObjectTypeOption {
  id: ObjectType;
  /** Short category name (e.g. "General Waste") — what gets stored/reported. */
  label: string;
  /** Helper text shown beside the bold name in the dropdown only. */
  description: string;
}

// Preserve the enum ordering (broad categories, most-common first) rather than
// sorting alphabetically, so the operator's muscle memory is stable.
const OBJECT_TYPE_OPTIONS: ObjectTypeOption[] = OBJECT_TYPES.map((id) => ({
  id,
  label: OBJECT_TYPE_LABELS[id],
  description: OBJECT_TYPE_DESCRIPTIONS[id],
}));

// Match the query against BOTH the category name and its description, so typing
// an example term ("tyres", "sofa", "battery") surfaces the owning category.
// The default Autocomplete filter only searches getOptionLabel (the name), so
// the descriptions we added would otherwise be unsearchable.
function filterObjectTypeOptions(options: readonly ObjectTypeOption[], query: string): ObjectTypeOption[] {
  const q = query.trim().toLowerCase();
  if (q === "") return [...options];
  return options.filter((o) => o.label.toLowerCase().includes(q) || o.description.toLowerCase().includes(q));
}

// Custom dropdown row: bold category name + muted description. Because a custom
// renderOption replaces the built-in AutocompleteOption entirely, we must
// re-create two things it provided: the focused-background highlight, and
// scrolling the focused row into view on web (otherwise arrow-key navigation
// can move the highlight below the visible area with these taller two-line rows).
function ObjectTypeOptionRow({ option, focused }: { option: ObjectTypeOption; focused: boolean }) {
  const { theme } = useUnistyles();
  const ref = useRef<ComponentRef<typeof View>>(null);

  useEffect(() => {
    if (focused && Platform.OS === "web" && ref.current) {
      (ref.current as unknown as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [focused]);

  return (
    <View ref={ref} px="3xl" py="xl" style={focused ? { backgroundColor: theme.colors.surface2.hover } : undefined}>
      <Text textStyle="sm/semibold" color="text">
        {option.label}
      </Text>
      <Text textStyle="xs/regular" color="textMuted">
        {option.description}
      </Text>
    </View>
  );
}

const DESCRIPTION_MAX_LENGTH = 500;

export interface ConfirmDumpFormValue {
  objectType: ObjectType;
  /** Reviewer's description of the detected dump. Always supplied. */
  description: string;
}

interface ConfirmDumpModalProps {
  visible: boolean;
  onClose: () => void;
  /**
   * Persist the confirmed dump. Resolves on success (parent handles navigation
   * + toast) and rejects on failure so the modal can surface an inline error
   * and stay open for a retry.
   */
  onConfirm: (value: ConfirmDumpFormValue) => Promise<void>;
  /** Drives the button spinner + disables inputs while the write is in flight. */
  isSubmitting: boolean;
}

/**
 * Modal form for confirming a dump: a mandatory waste-category dropdown plus a
 * mandatory free-text description. Submit stays disabled until the form
 * satisfies the same rule the API enforces (a category is selected and a
 * non-empty description is supplied), so a valid press always maps to a valid
 * request.
 */
export const ConfirmDumpModal = memo(({ visible, onClose, onConfirm, isSubmitting }: ConfirmDumpModalProps) => {
  const { theme } = useUnistyles();
  const [selected, setSelected] = useState<ObjectTypeOption | null>(null);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const trimmedDescription = description.trim();

  // Mirror the server-side contract: a category must be selected and the
  // description is always mandatory. Keeping this in lockstep with the contract
  // means the disabled state and the API never disagree.
  const isValid = selected !== null && trimmedDescription.length > 0;

  const reset = useCallback(() => {
    setSelected(null);
    setDescription("");
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    reset();
    onClose();
  }, [isSubmitting, reset, onClose]);

  const handleSubmit = useCallback(() => {
    if (!selected || !isValid || isSubmitting) return;
    setError(null);
    onConfirm({
      objectType: selected.id,
      // Description is always required, so always send the trimmed value.
      description: trimmedDescription,
    })
      .then(() => {
        reset();
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Couldn't record the dump. Please try again.");
      });
  }, [selected, isValid, isSubmitting, trimmedDescription, onConfirm, reset]);

  return (
    <DialogModal
      visible={visible}
      onClose={handleClose}
      showCloseButton={!isSubmitting}
      title="Confirm Dump"
      style={{ width: isWeb ? 460 : undefined }}
    >
      <Text textStyle="xs/regular" color="textMuted" mb="3xl">
        Record what was dumped in this clip. This is saved as evidence against the camera.
      </Text>

      <View mb="3xl" style={{ zIndex: 10 }}>
        <Autocomplete<ObjectTypeOption>
          options={OBJECT_TYPE_OPTIONS}
          value={selected}
          onChange={(v) => {
            setSelected(v);
            setError(null);
          }}
          getOptionLabel={(o) => o.label}
          getOptionId={(o) => o.id}
          label="Object detected"
          placeholder="Select what was dumped…"
          // Search names AND descriptions so example terms find their category.
          filterOptions={filterObjectTypeOptions}
          // Bold name + muted description. The row also restores the focused
          // highlight and scroll-into-view that the default option provides.
          renderOption={(o, state) => <ObjectTypeOptionRow option={o} focused={state.focused} />}
        />
      </View>

      <View mb={error ? "xl" : "5xl"}>
        <Input
          value={description}
          onChangeText={(t) => {
            setDescription(t);
            setError(null);
          }}
          label="Description"
          placeholder="Describe the detected dump"
          disabled={isSubmitting}
          // Note guiding the reviewer on what to write; grammar/spelling corrected
          // from the requested copy ("videoclip" -> "video clip").
          helperText="Describe what has been detected to help the customer identify the dump in the video clip."
          textInputProps={{
            multiline: true,
            // Four visible rows. numberOfLines is a hint on web/Android; the
            // explicit minHeight guarantees the taller box across platforms.
            numberOfLines: 4,
            maxLength: DESCRIPTION_MAX_LENGTH,
            // Top-align so multi-line text grows downward from the top edge
            // rather than vertically centring in the taller box.
            textAlignVertical: "top",
            // paddingTop gives the first row breathing room from the top edge /
            // floating label (single-line inputs are centred, so the shared
            // component adds no top padding that a top-aligned textarea needs).
            style: { minHeight: 112, paddingTop: 10 },
          }}
        />
      </View>

      {error && (
        <Text textStyle="xs/regular" mb="5xl" style={{ color: theme.colors.status.error }}>
          {error}
        </Text>
      )}

      <Button
        label="Confirm Dump"
        variant="primary"
        variantColor="blue"
        size="md"
        loading={isSubmitting}
        disabled={!isValid || isSubmitting}
        onPress={handleSubmit}
      />
    </DialogModal>
  );
});

ConfirmDumpModal.displayName = "ConfirmDumpModal";
